import { Award, BarChart, Globe, Link, Shield, TrendingUp } from 'lucide-react'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'

function SeoUrlAnalysisDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  // Skip schema validation - use CSV data directly like CSV Reader template tool
  const result = nodeExecution.outputs

  // Handle both array and object result formats
  const resultData = Array.isArray(result) ? result[0] : result

  // Get query context from structured result
  const url = resultData?.query?.url || ''
  const action = resultData?.query?.action || ''
  const _recordCount = resultData?.metadata?.recordCount || 0
  const executedAt = resultData?.metadata?.executedAt || new Date().toISOString()

  // Parse CSV from structured result
  const csvData = resultData?.csvData
  const { pageAuthorityScore, domainAuthorityScore, domain } = (() => {
    // Local CSV parser that supports semicolon delimiter and quotes
    const parseCsv = (csv: string): { headers: string[]; rows: string[][] } => {
      const lines = csv.split(/\r?\n/).filter((l) => l.trim().length > 0)
      const rows: string[][] = []
      for (const line of lines) {
        const fields: string[] = []
        let current = ''
        let inQuotes = false
        for (let i = 0; i < line.length; i++) {
          const ch = line[i]
          const next = line[i + 1]
          if (ch === '"') {
            if (inQuotes && next === '"') {
              current += '"'
              i++
            } else {
              inQuotes = !inQuotes
            }
          } else if (ch === ';' && !inQuotes) {
            fields.push(current)
            current = ''
          } else {
            current += ch
          }
        }
        fields.push(current)
        rows.push(fields)
      }
      const headers = rows.length > 0 ? rows[0].map((h) => h.trim()) : []
      const dataRows = rows.length > 1 ? rows.slice(1) : []
      return { headers, rows: dataRows }
    }

    const coerceNumber = (val: string | undefined): number => {
      if (!val) return 0
      const cleaned = val.replace(/[,\s]/g, '')
      const n = Number(cleaned)
      return Number.isFinite(n) ? n : 0
    }

    if (!csvData || typeof csvData !== 'string') {
      return {
        pageAuthorityScore: 0,
        domainAuthorityScore: 0,
        domain: '',
      }
    }

    const { headers, rows } = parseCsv(csvData)
    if (headers.length === 0 || rows.length === 0) {
      return {
        pageAuthorityScore: 0,
        domainAuthorityScore: 0,
        domain: '',
      }
    }

    // Build index map of headers (support both short codes and readable headers)
    const lowerHeaders = headers.map((h) => h.toLowerCase())
    const idx = (aliases: string[]): number => {
      for (const a of aliases) {
        const k = a.toLowerCase()
        const i = lowerHeaders.indexOf(k)
        if (i !== -1) return i
      }
      return -1
    }

    const first = rows[0]
    const pageAuthorityIdx = idx(['page_authority_score', 'page authority score'])
    const domainAuthorityIdx = idx(['domain_authority_score', 'domain authority score'])
    const domainIdx = idx(['domain'])

    return {
      pageAuthorityScore: pageAuthorityIdx !== -1 ? coerceNumber(first[pageAuthorityIdx]) : 0,
      domainAuthorityScore: domainAuthorityIdx !== -1 ? coerceNumber(first[domainAuthorityIdx]) : 0,
      domain: domainIdx !== -1 ? (first[domainIdx] || '').trim() : '',
    }
  })()

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600'
    if (score >= 60) return 'text-yellow-600'
    if (score >= 40) return 'text-orange-600'
    return 'text-red-600'
  }

  return (
    <div className="rounded-3xl border border-gray-200/50 bg-gradient-to-br from-white/90 to-purple-50/80 text-black shadow-xl backdrop-blur-[12px]">
      <div className="px-6 py-4">
        <div className="flex items-start gap-4">
          <div className="mt-1 flex-shrink-0">
            <div className="rounded-full bg-gradient-to-br from-purple-100 to-purple-200 p-3 shadow-sm">
              <Link className="h-6 w-6 text-purple-600" />
            </div>
          </div>
          <div className="min-w-0 flex-1">
            <div className="mb-3 flex items-center gap-3">
              <div className="rounded-md bg-gradient-to-r from-purple-500 to-indigo-600 p-1">
                <Shield className="h-4 w-4 text-white" />
              </div>
              <div className="bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-base font-bold text-gray-900 text-transparent">
                SEO Research - URL Analysis
              </div>
              <div className="ml-auto flex items-center gap-2 rounded-full bg-purple-100 px-3 py-1 text-xs font-medium text-purple-700">
                <Award className="h-3 w-3" />
                Authority Analysis
              </div>
            </div>

            {/* Query Context */}
            <div className="mb-4 rounded-xl border border-purple-100 bg-white/60 p-4 backdrop-blur-sm">
              <div className="grid grid-cols-1 gap-3">
                <div className="flex items-center gap-2">
                  <div className="rounded-md bg-purple-100 p-1">
                    <Link className="h-3 w-3 text-purple-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <span className="text-xs font-medium text-gray-500">Target URL</span>
                    <div className="break-all text-sm font-semibold text-gray-900">{url}</div>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <div className="flex items-center gap-2">
                    <div className="rounded-md bg-blue-100 p-1">
                      <BarChart className="h-3 w-3 text-blue-600" />
                    </div>
                    <div>
                      <span className="text-xs font-medium text-gray-500">Action Type</span>
                      <div className="text-sm font-semibold text-gray-900">{action}</div>
                    </div>
                  </div>
                  {domain && (
                    <div className="flex items-center gap-2">
                      <div className="rounded-md bg-green-100 p-1">
                        <Globe className="h-3 w-3 text-green-600" />
                      </div>
                      <div>
                        <span className="text-xs font-medium text-gray-500">Domain</span>
                        <div className="text-sm font-semibold text-gray-900">{domain}</div>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <div className="rounded-md bg-orange-100 p-1">
                      <TrendingUp className="h-3 w-3 text-orange-600" />
                    </div>
                    <div>
                      <span className="text-xs font-medium text-gray-500">Executed</span>
                      <div className="text-sm font-semibold text-gray-900">
                        {new Date(executedAt).toLocaleTimeString()}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Authority Scores */}
            <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="rounded-xl border border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 p-2">
                      <TrendingUp className="h-4 w-4 text-white" />
                    </div>
                    <div>
                      <div className="mb-1 text-xs font-medium text-blue-600">Page Authority</div>
                      <div className={`text-2xl font-bold ${getScoreColor(pageAuthorityScore)}`}>
                        {pageAuthorityScore}
                        <span className="text-sm font-normal text-gray-500">/100</span>
                      </div>
                    </div>
                  </div>
                  <div className="h-2 w-20 rounded-full bg-gray-200">
                    <div
                      className={`h-2 rounded-full transition-all duration-500 ${
                        pageAuthorityScore >= 80
                          ? 'bg-green-500'
                          : pageAuthorityScore >= 60
                            ? 'bg-yellow-500'
                            : pageAuthorityScore >= 40
                              ? 'bg-orange-500'
                              : 'bg-red-500'
                      }`}
                      style={{ width: `${pageAuthorityScore}%` }}
                    />
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-green-200 bg-gradient-to-r from-green-50 to-emerald-50 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="rounded-full bg-gradient-to-r from-green-500 to-emerald-500 p-2">
                      <Shield className="h-4 w-4 text-white" />
                    </div>
                    <div>
                      <div className="mb-1 text-xs font-medium text-green-600">
                        Domain Authority
                      </div>
                      <div className={`text-2xl font-bold ${getScoreColor(domainAuthorityScore)}`}>
                        {domainAuthorityScore}
                        <span className="text-sm font-normal text-gray-500">/100</span>
                      </div>
                    </div>
                  </div>
                  <div className="h-2 w-20 rounded-full bg-gray-200">
                    <div
                      className={`h-2 rounded-full transition-all duration-500 ${
                        domainAuthorityScore >= 80
                          ? 'bg-green-500'
                          : domainAuthorityScore >= 60
                            ? 'bg-yellow-500'
                            : domainAuthorityScore >= 40
                              ? 'bg-orange-500'
                              : 'bg-red-500'
                      }`}
                      style={{ width: `${domainAuthorityScore}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Authority Score Interpretation */}
            <div className="rounded-xl border border-purple-100 bg-white/60 p-4 backdrop-blur-sm">
              <div className="flex items-center gap-3">
                <div className="rounded-full bg-gradient-to-r from-purple-100 to-indigo-100 p-2">
                  <Award className="h-4 w-4 text-purple-600" />
                </div>
                <div>
                  <div className="mb-1 text-xs font-medium text-gray-600">SEO Assessment</div>
                  <div className="text-sm font-semibold">
                    {pageAuthorityScore >= 70 || domainAuthorityScore >= 70 ? (
                      <span className="text-green-600">
                        🎯 High authority - excellent for link building
                      </span>
                    ) : pageAuthorityScore >= 40 || domainAuthorityScore >= 40 ? (
                      <span className="text-yellow-600">
                        📈 Moderate authority - good potential
                      </span>
                    ) : (
                      <span className="text-blue-600">
                        🔨 Building authority - monitor progress
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function downloadSeoUrlAnalysis(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function seoUrlAnalysisToMarkdown(nodeExecution: NodeExecution): string {
  // Skip schema validation - use CSV data directly like CSV Reader template tool
  const result = nodeExecution.outputs
  const resultData = Array.isArray(result) ? result[0] : result
  const url = resultData?.query?.url || ''

  let pageAuthorityScore = 0
  let domainAuthorityScore = 0
  let domain = ''

  const csvData = resultData?.csvData
  if (csvData && typeof csvData === 'string') {
    const parseCsv = (csv: string): { headers: string[]; rows: string[][] } => {
      const lines = csv.split(/\r?\n/).filter((l) => l.trim().length > 0)
      const rows: string[][] = []
      for (const line of lines) {
        const fields: string[] = []
        let current = ''
        let inQuotes = false
        for (let i = 0; i < line.length; i++) {
          const ch = line[i]
          const next = line[i + 1]
          if (ch === '"') {
            if (inQuotes && next === '"') {
              current += '"'
              i++
            } else {
              inQuotes = !inQuotes
            }
          } else if (ch === ';' && !inQuotes) {
            fields.push(current)
            current = ''
          } else {
            current += ch
          }
        }
        fields.push(current)
        rows.push(fields)
      }
      const headers = rows.length > 0 ? rows[0].map((h) => h.trim()) : []
      const dataRows = rows.length > 1 ? rows.slice(1) : []
      return { headers, rows: dataRows }
    }

    const coerceNumber = (val: string | undefined): number => {
      if (!val) return 0
      const cleaned = val.replace(/[,\s]/g, '')
      const n = Number(cleaned)
      return Number.isFinite(n) ? n : 0
    }

    const { headers, rows } = parseCsv(csvData)
    if (headers.length > 0 && rows.length > 0) {
      const lowerHeaders = headers.map((h) => h.toLowerCase())
      const idx = (aliases: string[]): number => {
        for (const a of aliases) {
          const k = a.toLowerCase()
          const i = lowerHeaders.indexOf(k)
          if (i !== -1) return i
        }
        return -1
      }
      const first = rows[0]
      const pageAuthorityIdx = idx(['page_authority_score', 'page authority score'])
      const domainAuthorityIdx = idx(['domain_authority_score', 'domain authority score'])
      const domainIdx = idx(['domain'])

      pageAuthorityScore = pageAuthorityIdx !== -1 ? coerceNumber(first[pageAuthorityIdx]) : 0
      domainAuthorityScore = domainAuthorityIdx !== -1 ? coerceNumber(first[domainAuthorityIdx]) : 0
      domain = domainIdx !== -1 ? (first[domainIdx] || '').trim() : ''
    }
  }

  return `## SEO Research - URL Analysis

**URL:** ${url}
${domain ? `**Domain:** ${domain}` : ''}

### Authority Scores
**Page Authority:** ${pageAuthorityScore}/100
**Domain Authority:** ${domainAuthorityScore}/100


---
`
}

export const SeoUrlAnalysisDisplay = createWorkflowToolComponent(
  SeoUrlAnalysisDisplayComponent,
  downloadSeoUrlAnalysis,
  seoUrlAnalysisToMarkdown
)
